import React from "react";
import { Box, IconButton, Stack, Typography, Chip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { ArmeniaAct } from "./ArmeniaActActions";

type Props = {
  acts: ArmeniaAct[];
  onOpenAct: (act: ArmeniaAct) => void;
};

const getStatusColor = (status?: string) => {
  switch (status) {
    case "DISAGREEMENT":
      return "warning";
    case "SIGNED":
    case "AGREED":
    case "APPROVED":
      return "success";
    case "EXPIRED":
      return "error";
    default:
      return "default";
  }
};

const ArmeniaActsDrawerContent = ({ acts, onOpenAct }: Props) => {
  const { t } = useTranslation();

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case "DISAGREEMENT":
        return t("armeniaAct.status.disagreement");
      case "SIGNED":
        return t("armeniaAct.status.signed");
      case "EXPIRED":
        return t("armeniaAct.status.expired");
      case "APPROVED":
        return t("armeniaAct.status.approved");
      case "DRAFT":
        return t("armeniaAct.status.draft");
      case "AGREED":
        return t("armeniaAct.status.agreed");
      default:
        return status || "";
    }
  };

  const columns: GridColDef<ArmeniaAct>[] = [
    {
      field: "act_date",
      headerName: t("armeniaAct.drawer.createdAt"),
      flex: 1,
      valueGetter: (_, row) =>
        row.act_date ? dayjs(row.act_date).format("DD.MM.YYYY") : "",
    },
    {
      field: "id",
      headerName: t("armeniaAct.drawer.act"),
      flex: 0.7,
    },
    {
      field: "version",
      headerName: t("armeniaAct.drawer.version"),
      flex: 0.6,
    },
    {
      field: "status",
      headerName: t("armeniaAct.drawer.status"),
      flex: 1,
      renderCell: (params) => (
        <Chip
          size="small"
          label={getStatusLabel(params.row.status)}
          color={getStatusColor(params.row.status) as any}
          variant={params.row.status === "DRAFT" ? "outlined" : "filled"}
        />
      ),
    },
    {
      field: "updated_at",
      headerName: t("armeniaAct.drawer.updatedAt"),
      flex: 1,
      valueGetter: (_, row: any) =>
        row.updated_at ? dayjs(row.updated_at).format("DD.MM.YYYY HH:mm") : "",
    },
    {
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          title={t("armeniaAct.drawer.openAct")}
          onClick={() => onOpenAct(params.row)}
        >
          <VisibilityIcon color="primary" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2">
          {t("armeniaAct.drawer.totalActs")}: <strong>{acts.length}</strong>
        </Typography>
      </Stack>

      <DataGrid
        rows={acts}
        columns={columns}
        getRowId={(row) => row.id}
        autoHeight
        hideFooter
        sx={{
          "--DataGrid-rowBorderColor": "#D1D3D4",
          "& .MuiDataGrid-cell": {
            fontSize: 14,
            borderTop: "0.5px solid #D1D3D4",
          },
          fontFamily: "inherit",
          borderWidth: 0,
          background: "#ffffff",
        }}
      />
    </Box>
  );
};

export default ArmeniaActsDrawerContent;
